import { useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DillButton, DillModal, DillToggle, DillAvatar } from '../../../../general/Dill';
import { UsersThree, Trash, EnvelopeSimple } from 'phosphor-react';
import { User } from 'react-feather';
import { useAppDispatch } from '../../../../../redux/store';
import { openMessageModal } from '../../../../../redux/globalSlices/genericSlice';
import {
  supplierUpdateBuyerUserPermissions,
  supplierDeleteBuyerUser,
} from '../../../../../redux/services/supplierBuyersService';
import { supplierResendBuyerUserInvite } from '../../../../../redux/services/buyersService';
import { openDillToast } from '../../../../../utils/helpers';
import SupplierCustomerInviteUserModal from '../SupplierCustomerInviteUserModal/SupplierCustomerInviteUserModal';
import { BuyerUserPermissions, DEFAULT_BUYER_USER_PERMISSIONS } from '@dill/dill-shared';

type UserEmailDetail = {
  email: string;
  firstName: string;
  lastName: string;
  userId: string;
  isPasswordSet?: boolean;
  isInvitedUser: boolean;
  isStatementsEmailOptOut: boolean;
  isPaymentRemindersEmailOptOut: boolean;
  permissions?: BuyerUserPermissions;
};

interface AccountAccessModalProps {
  open: boolean;
  buyerName: string;
  supplierId: string;
  buyerId: string;
  usersEmailsDetails: UserEmailDetail[];
  onRefresh: () => void;
  onClose: () => void;
}

const AccountAccessModal = ({
  open,
  buyerName,
  supplierId,
  buyerId,
  usersEmailsDetails,
  onRefresh,
  onClose,
}: AccountAccessModalProps) => {
  const dispatch = useAppDispatch();
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);

  const getErrorMessage = (payload: unknown, fallback: string): string => {
    if (typeof payload === 'string') return payload;
    if (payload && typeof payload === 'object') {
      const p = payload as Record<string, unknown>;
      if (typeof p.message === 'string') return p.message;
      if (Array.isArray(p.errors) && p.errors.length > 0) {
        const firstError = p.errors[0];
        if (typeof firstError === 'string') return firstError;
        if (
          firstError &&
          typeof firstError === 'object' &&
          typeof (firstError as Record<string, unknown>).msg === 'string'
        ) {
          return (firstError as Record<string, unknown>).msg as string;
        }
      }
    }
    return fallback;
  };

  const handleUpdateUserPermissions = async (userId: string, permissions: BuyerUserPermissions) => {
    setUpdatingUserId(userId);
    const res = await dispatch(
      supplierUpdateBuyerUserPermissions({ supplierId, buyerId, userId, permissions })
    );
    setUpdatingUserId(null);
    if (res.type === 'supplierBuyers/supplierUpdateBuyerUserPermissions/fulfilled') {
      onRefresh();
    } else {
      const errorMsg = getErrorMessage(res.payload, 'Failed to update permissions');
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  const handleDeleteUser = async (userId: string) => {
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Removing user',
        subTitle: 'Removing user from this customer...',
      })
    );
    const res = await dispatch(supplierDeleteBuyerUser({ supplierId, buyerId, userId }));
    if (res.type === 'supplierBuyers/supplierDeleteBuyerUser/fulfilled') {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'SUCCESS',
          title: 'User Removed',
          subTitle: 'The user has been removed from this customer',
        })
      );
      onRefresh();
    } else {
      const errorMsg = getErrorMessage(res.payload, 'Failed to remove user');
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  const handleResendInvite = async (userDetail: UserEmailDetail) => {
    setResendingUserId(userDetail.userId);
    const res = await dispatch(
      supplierResendBuyerUserInvite({ supplierId, buyerId, userId: userDetail.userId })
    );
    setResendingUserId(null);
    if (res.type === 'buyers/supplierResendBuyerUserInvite/fulfilled') {
      openDillToast({
        message: `The invite has been resent to ${userDetail.email}`,
        type: 'SUCCESS',
      });
    } else {
      const errorMsg = getErrorMessage(res.payload, 'Failed to resend invite');
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error',
          subTitle: errorMsg,
        })
      );
    }
  };

  if (!open) return null;

  return (
    <>
      <DillModal
        open
        onClose={onClose}
        title="Account Access"
        subtitle={buyerName}
        maxWidth="760px"
        footer={<DillButton variant="secondary" label="Close" onClick={onClose} />}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UsersThree size={16} weight="duotone" className="text-blue-500" />
              <span className="text-xs font-bold text-gray-900">
                {usersEmailsDetails.length || 0} user
                {usersEmailsDetails.length !== 1 ? 's' : ''}
              </span>
            </div>
            <DillButton
              label="Invite Users"
              variant="secondary"
              icon={<User size={14} />}
              height={28}
              onClick={() => setIsInviteUserModalOpen(true)}
            />
          </div>
          <div className="grid grid-cols-[1fr_180px_100px_64px] gap-2 items-center px-2 pb-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Users
            </span>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Viewer
              </span>
              <span className="text-[10px] text-gray-400 whitespace-nowrap">
                Can retrieve invoices &amp; statements
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Payer
              </span>
              <span className="text-[10px] text-gray-400 whitespace-nowrap">Can pay online</span>
            </div>
            <span />
          </div>
          <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
            {usersEmailsDetails.map((userDetail, index) => {
              const permissions = userDetail.permissions || DEFAULT_BUYER_USER_PERMISSIONS;
              const hasPayer = permissions.includes('PAYER');
              const isLoading = updatingUserId === userDetail.userId;
              const isResending = resendingUserId === userDetail.userId;
              return (
                <div
                  key={userDetail.userId}
                  className="grid grid-cols-[1fr_180px_100px_64px] gap-2 items-center p-2 rounded-lg border border-gray-50 hover:border-gray-200 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <DillAvatar name={userDetail.firstName || userDetail.email} idx={index} />
                    <div className="min-w-0">
                      {userDetail.firstName || userDetail.lastName ? (
                        <p className="text-xs font-medium text-gray-900 truncate capitalize">
                          {`${userDetail.firstName} ${userDetail.lastName}`.trim()}
                        </p>
                      ) : (
                        <p className="text-xs font-medium text-gray-900 break-all">
                          {userDetail.email}
                        </p>
                      )}
                      {(userDetail.firstName || userDetail.lastName) && (
                        <p className="text-[10px] text-gray-400 truncate">{userDetail.email}</p>
                      )}
                      <p className="text-[10px] text-gray-400">
                        {userDetail.isPasswordSet ? 'Active' : 'Invited'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <DillToggle value isDisabled onChange={() => {}} />
                  </div>
                  <div className="flex items-center">
                    {isLoading ? (
                      <span className="w-3.5 h-3.5 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <DillToggle
                        value={hasPayer}
                        onChange={(v) => {
                          handleUpdateUserPermissions(
                            userDetail.userId,
                            v ? ['VIEWER', 'PAYER'] : ['VIEWER']
                          );
                        }}
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      {!userDetail?.isPasswordSet &&
                        (isResending ? (
                          <span className="w-3.5 h-3.5 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span
                            data-tooltip-id="resend-invite-tooltip"
                            data-tooltip-content="Resend invite">
                            <DillButton
                              variant="icon-blue"
                              icon={<EnvelopeSimple size={16} weight="bold" />}
                              onClick={() => handleResendInvite(userDetail)}
                            />
                          </span>
                        ))}
                    </div>
                    <DillButton
                      variant="icon-red"
                      icon={<Trash size={16} weight="bold" />}
                      onClick={() =>
                        dispatch(
                          openMessageModal({
                            buttonText: 'Cancel',
                            isSecondButton: true,
                            secondButtonText: 'Yes, remove',
                            modalType: 'WARN',
                            title: 'Remove Team Member',
                            subTitle: `Are you sure you want to remove ${
                              userDetail.email || 'this user'
                            } from this customer?`,
                            onSecondButtonClick: () => {
                              handleDeleteUser(userDetail.userId);
                            },
                          })
                        )
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <ReactTooltip
          id="resend-invite-tooltip"
          place="top"
          variant="dark"
          opacity={'100%'}
          style={{
            background: '#334155',
            zIndex: 10000,
            borderRadius: '8px',
            fontWeight: '600',
            maxWidth: '220px',
            fontSize: '12px',
          }}
        />
      </DillModal>
      {isInviteUserModalOpen && (
        <SupplierCustomerInviteUserModal
          open={isInviteUserModalOpen}
          selectedBuyerId={buyerId || null}
          handleClose={() => {
            setIsInviteUserModalOpen(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default AccountAccessModal;
